import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { LoaderCircle } from 'lucide-react';
import { cn } from '@shared/lib/utils';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(163,163,163,0.5)]';

const focusRingDestructive =
  'focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-[0_0_0_3px_rgba(220,38,38,0.2)]';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap font-medium',
    'transition-colors duration-fast ease-standard',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
  ].join(' '),
  {
    variants: {
      variant: {
        default: [
          'bg-base-primary text-base-primary-foreground shadow-xs',
          focusRing,
          'hover:bg-[color-mix(in_srgb,var(--base-primary),white_10%)]',
        ].join(' '),
        secondary: [
          'bg-[var(--base-sidebar-accent,#EEF2F7)] text-[var(--button-secondary-foreground,#171717)]',
          focusRing,
          'hover:bg-[color-mix(in_srgb,var(--base-sidebar-accent,#EEF2F7),white_20%)]',
          'dark:bg-base-secondary dark:text-[var(--button-secondary-foreground,#27303B)]',
          'dark:hover:bg-[color-mix(in_srgb,var(--base-secondary),white_8%)]',
        ].join(' '),
        outline: [
          'border border-base-border bg-background text-base-foreground',
          focusRing,
          'hover:bg-base-accent hover:text-base-foreground',
          'dark:border-[rgba(255,255,255,0.15)] dark:bg-[rgba(255,255,255,0.045)] dark:text-base-foreground dark:hover:bg-[rgba(255,255,255,0.08)]',
        ].join(' '),
        ghost: [
          'text-base-foreground',
          focusRing,
          'hover:bg-base-accent hover:text-base-foreground',
          'dark:text-base-foreground dark:hover:bg-[rgba(255,255,255,0.08)]',
        ].join(' '),
        destructive: [
          'bg-destructive text-[var(--destructive-button-foreground,#FEF2F2)] shadow-xs',
          focusRingDestructive,
          'hover:bg-destructive/90',
        ].join(' '),
        link: [
          'text-base-foreground underline-offset-4',
          focusRing,
          'hover:underline',
        ].join(' '),
        /** @deprecated Use `default` */
        brand: '',
        /** @deprecated Use `default` + `size="lg"` */
        'login-dark': '',
      },
      size: {
        default:
          'h-10 rounded-lg px-4 py-2.5 text-sm leading-5 [&_svg]:size-4',
        sm: 'h-8 rounded-md px-3 py-2 text-xs leading-4 [&_svg]:size-4',
        lg: 'h-14 rounded-lg px-8 py-3 text-base font-normal leading-6 [&_svg]:size-4',
        icon: 'size-10 rounded-lg p-0 [&_svg]:size-4',
        'icon-sm': 'size-8 rounded-md p-0 [&_svg]:size-4',
      },
    },
    compoundVariants: [
      {
        variant: ['default', 'brand', 'login-dark'],
        className: [
          'bg-base-primary text-base-primary-foreground shadow-xs',
          focusRing,
          'hover:bg-[color-mix(in_srgb,var(--base-primary),white_10%)]',
        ].join(' '),
      },
      {
        variant: 'default',
        size: 'lg',
        className:
          'hover:bg-[color-mix(in_srgb,var(--base-primary),white_20%)]',
      },
      {
        variant: ['brand', 'login-dark'],
        size: 'lg',
        className:
          'hover:bg-[color-mix(in_srgb,var(--base-primary),white_20%)]',
      },
      {
        variant: 'destructive',
        size: 'lg',
        className: 'gap-3 [&_svg]:size-5',
      },
      {
        variant: 'outline',
        size: ['default', 'icon'],
        className: 'disabled:shadow-xs',
      },
      {
        variant: 'secondary',
        size: ['default', 'lg'],
        className: 'disabled:shadow-xs',
      },
    ],
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const loadingAppearance = [
  '!h-10 !rounded-lg !border-transparent !bg-base-primary !px-4 !py-2.5',
  '!text-sm !font-medium !leading-5 !text-base-primary-foreground !shadow-xs',
  'hover:!bg-base-primary sm:!h-10 [&_svg]:!size-4',
].join(' ');

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      leftIcon,
      rightIcon,
      children,
      loading = false,
      disabled,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    const content = (
      <>
        {loading ? (
          <LoaderCircle className="animate-spin" strokeWidth={1.33} aria-hidden />
        ) : (
          leftIcon
        )}
        {children}
        {!loading && rightIcon}
      </>
    );

    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size }), className)}
          ref={ref}
          aria-disabled={isDisabled || undefined}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        type={type}
        className={cn(buttonVariants({ variant, size }), className, loading && loadingAppearance)}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {content}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
